import { OrbitControls, PointerLockControls } from '@react-three/drei'
import { OrbitControls as ThreeOrbitControls, PointerLockControls as ThreePointerLockControls } from 'three-stdlib'
import { useEffect, memo, useRef, useCallback, useState } from 'react'
import * as THREE from 'three'
import { config } from '../../config'
import { distanceBetween, getTouchPoints } from '../utils'
import { useThree, useFrame } from '@react-three/fiber'

type CameraControllerProps = {
  refCanvas: React.RefObject<HTMLCanvasElement>
  id: number | undefined
  controlsMode: number
  fov?: number | undefined | null
  cameraReset?: number | undefined | null
  onLongPressDone?: () => void | undefined | null
  onLongPressProgress?: (s:boolean, rest:number, option:any) => void | undefined | null
  onAutoPilot?: (s:boolean) => void | undefined | null
}

export const CameraController = memo((props: CameraControllerProps) => {
  const {
    refCanvas, id, controlsMode, fov = 75, cameraReset = 0,
    onLongPressDone = () => {},
    onLongPressProgress = (s:boolean, rest:number, option:any) => {},
    onAutoPilot = (s:boolean) => {}
  } = props
  const [isPinching, setIsPinching] = useState(false)
  const [isAnimateZoom, setIsAnimateZoom] = useState(false)
  const [isTouchDevice, setIsTouchDevice] = useState(false)
  const [tempScale, setTempScale] = useState(1.0)
  const [initialPinchPoints, setInitialPinchPoints] = useState([
    { x: 0, y: 0 },
    { x: 0, y: 0 },
  ])
  const [controlsEnabled, setControlsEnabled] = useState(true)
  const ref = useRef<ThreeOrbitControls>(null)
  const refPl = useRef<ThreePointerLockControls>(null)
  const refTargetZoom = useRef(config.ZOOM.DEFAULT)
  const refAnimationLoop = useRef<number | null>(null)
  // - TODO:
  const refLastOperated = useRef<number>(Date.now())
  const refAutoPilot = useRef<boolean>(false)
  const refCtlsMode = useRef<number | null>(null)
  const refDetector = useRef<boolean>(false)
  const refTime = useRef<number>(Date.now())
  const refIntervalTimer = useRef<number | null>(null)
  const refInitialPressPoints = useRef<Array<any> | null>([])
  const refCameraFov = useRef<number>(75)
  const refResetRequest = useRef<boolean>(false)
  const refQDelta = useRef<number>(0)
  // --

  const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

  useEffect(() => {
    refCameraFov.current = fov
  }, [fov])

  useEffect(() => {
    if (controlsMode < 1) return

    const af = async () => {
      setControlsEnabled(false)
      refQDelta.current = 0
      refAutoPilot.current = false
      refResetRequest.current = true
      await sleep(1000)
      refResetRequest.current = false
      setControlsEnabled(true)
    }

    af()
  }, [cameraReset, controlsMode])

  useEffect(() => {
    const rootElement = document.getElementById('root')
    if (!rootElement) return

    const _isTouchDevice = !!(
      'ontouchstart' in window ||
        ((navigator as any).pointerEnabled && navigator.maxTouchPoints > 0)
    )

    if (_isTouchDevice) setIsTouchDevice(true)
    //- DEBUG:
    // setIsTouchDevice(true)

    const handleResize = () => {
      const canvas = refCanvas.current
      if (!canvas) return
      const width = canvas.offsetWidth
      refTargetZoom.current = width <= config.MOBILE_WIDTH ? config.ZOOM.DEFAULT_MOBILE : config.ZOOM.DEFAULT
    }
    handleResize()

    const observer = new ResizeObserver(() => {
      handleResize()
    })
    observer.observe(rootElement)

    return () => {
      observer.disconnect()
    }
  }, [])

  useEffect(() => {
    if (refIntervalTimer.current) clearInterval(refIntervalTimer.current)

    refIntervalTimer.current = setInterval(() => {
      // TODO:
      if (refCtlsMode.current == 0 && refDetector.current == true) {
        const now = Date.now()
        const diff = now - refTime.current
        if (diff > 3000) {
          onLongPressDone()
          refDetector.current = false
        } else {
          onLongPressProgress(true, diff / 3000, null);
        }
      }
    }, 200);

    return () => {
      if (refIntervalTimer.current) clearInterval(refIntervalTimer.current)
    }
  }, [onLongPressDone, onLongPressProgress])

  // For PC: Zoom settings based on scroll events
  const onWheel = useCallback((event: WheelEvent) => {
    event.preventDefault()
    if (event.deltaY == 0) return

    const isPinch = !!(event.deltaY % 1) // Pinch-in on a trackpad
    if (isPinch) {
      trackPadMove(event, isTouchDevice ? true : false)
      return
    }
    wheelMove(event)
  }, [isTouchDevice])

  // Zoom settings with the mouse wheel
  const wheelMove = (event: WheelEvent) => {
    const zoomAdjustment = 0.1
    const _min = config.ZOOM.MIN
    const _max = config.ZOOM.MAX
    if (event.deltaY > 0) {
      refTargetZoom.current = Math.max(refTargetZoom.current - zoomAdjustment, _min)
    }
    if (event.deltaY < 0) {
      refTargetZoom.current = Math.min(refTargetZoom.current + zoomAdjustment, _max)
    }
    if (!isAnimateZoom) animateZoom()
  }

  // Zoom settings with a trackpad
  const trackPadMove = (event: WheelEvent, isTouchDevice: boolean = true) => {
    if (isTouchDevice) {
      if (!ref.current) return
    } else {
      if (!refPl.current) return
    }

    const zoomAdjustment = 0.007
    const camera = isTouchDevice ? (ref.current.object as THREE.PerspectiveCamera) : (refPl.current.camera as THREE.PerspectiveCamera)

    let scale = camera.zoom - event.deltaY * zoomAdjustment
    scale = Math.max(scale, config.ZOOM.MIN)
    scale = Math.min(scale, config.ZOOM.MAX)
    refTargetZoom.current = scale
    camera.zoom = scale

    camera.updateProjectionMatrix()
  }

  // For mobile: Zoom settings with pinch-in and pinch-out gestures
  const onStart = useCallback((event: TouchEvent) => {
    if (!ref.current || event.touches.length < 2) return

    ref.current.enabled = false
    ref.current.enableDamping = false
    ref.current.update()

    setIsPinching(true)
    const start = getTouchPoints(event)
    setInitialPinchPoints(start)
    setTempScale(refTargetZoom.current)
  }, [])

  // For mobile: During pinch-in and pinch-out gestures
  const onMove = useCallback(
    (event: TouchEvent) => {
      event.preventDefault()
      if (ref.current && ref.current.autoRotate) ref.current.autoRotate = false
      if (!ref.current || !isPinching || event.touches.length < 2) return

      const points = getTouchPoints(event)
      const camera = ref.current.object as THREE.PerspectiveCamera
      const initialDistance = distanceBetween(initialPinchPoints[0], initialPinchPoints[1])
      const currentDistance = distanceBetween(points[0], points[1])
      let scale = (currentDistance / initialDistance) * tempScale

      scale = Math.max(scale, config.ZOOM.MIN)
      scale = Math.min(scale, config.ZOOM.MAX)
      refTargetZoom.current = scale

      camera.zoom = scale
      camera.updateProjectionMatrix()
    },
    [isPinching, initialPinchPoints, tempScale]
  )

  // For mobile: End of pinch-in and pinch-out gestures
  const onEnd = useCallback(() => {
    if (!ref.current) return

    // Resume the default behavior of controls
    ref.current.enabled = true
    ref.current.enableDamping = true
    ref.current.update()

    setIsPinching(false)
  }, [])

  // Zoom easing animation
  const animateZoom = useCallback(() => {
    if (refAnimationLoop.current) {
      cancelAnimationFrame(refAnimationLoop.current)
    }

    let camera = null

    if (isTouchDevice) {
      if (!ref.current) return
      camera = ref.current.object
    } else {
      if (!refPl.current) return
      camera = refPl.current.camera as THREE.PerspectiveCamera
    }

    const zoomAdjustment = 0.14 // easeの調整量

    camera.zoom += (refTargetZoom.current - camera.zoom) * zoomAdjustment
    camera.updateProjectionMatrix()

    if (Math.abs(refTargetZoom.current - camera.zoom) < 0.0001) {
      setIsAnimateZoom(false)
      return
    }

    setIsAnimateZoom(true)
    refAnimationLoop.current = requestAnimationFrame(animateZoom)
  }, [isTouchDevice])

  const onUserInteract = useCallback(() => {
    refLastOperated.current = Date.now()
    refAutoPilot.current = false
    onAutoPilot(refAutoPilot.current)

    if (ref.current) {
      ref.current.autoRotate = false
      ref.current.update()
    }
  }, [])

  const detectLongPress = useCallback((e: any) => {
    if (/^.*end|cancel|up$/.test(e.type) && refDetector.current) {
      refDetector.current = false
      console.log('cancel detection by ', e.type)
      onLongPressProgress(false, 0, null)
    } else if (/^.*move$/.test(e.type) && refDetector.current) {
      if (refInitialPressPoints.current) {
        if (e instanceof PointerEvent) {
          if (Math.abs(e.movementX) < 3 && Math.abs(e.movementY) < 3) return
        } else {
          if (e.touches.length == 1) {
            const tp = getTouchPoints(e)
            if (distanceBetween(tp[0], refInitialPressPoints.current[0]) < 10) return
          }
        }
      }
      refDetector.current = false
      console.log('cancel detection by ', e.type)
      onLongPressProgress(false, 0, null)
    } else if (/^.*down|start$/.test(e.type)) {
      if (e instanceof PointerEvent) {
        const { clientX, clientY } = e as PointerEvent
        refInitialPressPoints.current = [{ x: clientX, y: clientY } ]
      } else {
        if (e.touches.length == 1) refInitialPressPoints.current = getTouchPoints(e)
      }
      refTime.current = Date.now()
      refDetector.current = true
      onLongPressProgress(true, 0, { type: e.type, position: refInitialPressPoints.current })
      console.log('start long press detection', e.type, refTime.current, e)
    }
  }, [onLongPressProgress])

  useEffect(() => {
    if (!refCanvas.current) return
    const _isTouchDevice = !!(
      'ontouchstart' in window ||
        ((navigator as any).pointerEnabled && navigator.maxTouchPoints > 0)
    )

    if (_isTouchDevice) {
      refCanvas.current.addEventListener('wheel', onWheel, { passive: false })
      refCanvas.current.addEventListener('touchmove', onMove, { passive: false })
      refCanvas.current.addEventListener('touchstart', onStart, { passive: true })
      refCanvas.current.addEventListener('touchend', onEnd, { passive: true })
      refCanvas.current.addEventListener('touchcancel', onEnd, { passive: true })
      //
      // refCanvas.current.addEventListener('touchmove', detectLongPress, { passive: false })
      // refCanvas.current.addEventListener('touchstart', detectLongPress, { passive: true })
      // refCanvas.current.addEventListener('touchend', detectLongPress, { passive: true })
      // refCanvas.current.addEventListener('touchcancel', detectLongPress, { passive: true })
    } else {
      document.addEventListener('wheel', onWheel, { passive: false })
      //
      // refCanvas.current.addEventListener('pointermove', detectLongPress, { passive: false })
      // refCanvas.current.addEventListener('pointerdown', detectLongPress, { passive: true })
      // refCanvas.current.addEventListener('pointerup', detectLongPress, { passive: true })
    }

    ['pointermove', 'touchmove', 'wheel'].forEach(
      (event) => refCanvas.current.addEventListener(event, onUserInteract, { passive: event == 'wheel' })
    )

    return () => {
      if (!refCanvas.current) return

      if (_isTouchDevice) {
        refCanvas.current.removeEventListener('wheel', onWheel)
        refCanvas.current.removeEventListener('touchmove', onMove)
        refCanvas.current.removeEventListener('touchstart', onStart)
        refCanvas.current.removeEventListener('touchend', onEnd)
        refCanvas.current.removeEventListener('touchcancel', onEnd)
        //
        // refCanvas.current.removeEventListener('touchmove', detectLongPress)
        // refCanvas.current.removeEventListener('touchstart', detectLongPress)
        // refCanvas.current.removeEventListener('touchsend', detectLongPress)
        // refCanvas.current.removeEventListener('touchcancel', detectLongPress)
      } else {
        document.removeEventListener('wheel', onWheel)
        //
        // refCanvas.current.removeEventListener('pointermove', detectLongPress)
        // refCanvas.current.removeEventListener('pointerdown', detectLongPress)
        // refCanvas.current.removeEventListener('pointerup', detectLongPress)
      }

      ['pointermove', 'touchmove', 'wheel'].forEach(
        (event) => refCanvas.current.removeEventListener(event, onUserInteract)
      )
    }
  }, [isPinching, refTargetZoom.current])

  useEffect(() => {
    console.log('ctls:mode', controlsMode)
    refCtlsMode.current = controlsMode

    if (controlsMode >= 2) {
      try {
        document.exitPointerLock()
      } catch(err) {
        console.error('no need unclock', err)
      }
    }
  }, [controlsMode])

  useFrame(() => {
    if (refResetRequest.current) {
      const _alpha_c = Math.min(refQDelta.current += (Math.pow(1 - refQDelta.current, 2) * 0.01), 1)

      if (ref.current) {
        ref.current.autoRotate = false

        let { x, y, z } = ref.current.target
        let { x: x2, y: y2, z: z2 } = ref.current.object.position
        y = THREE.MathUtils.lerp(y, 0, _alpha_c)
        x = THREE.MathUtils.lerp(x, 0, _alpha_c)
        z = THREE.MathUtils.lerp(z, 0, _alpha_c)
        y2 = THREE.MathUtils.lerp(y2, 0, _alpha_c)
        x2 = THREE.MathUtils.lerp(x2, 0, _alpha_c)
        z2 = THREE.MathUtils.lerp(z2, 1, _alpha_c)

        const cam = ref.current.object as THREE.PerspectiveCamera
        cam.zoom = THREE.MathUtils.lerp(cam.zoom, config.ZOOM.DEFAULT, _alpha_c)

        ref.current.target.set(x, y, z)
        ref.current.object.position.set(x2, y2, z2)
        cam.updateProjectionMatrix()
        ref.current.update()
      }

      if (refPl.current) {
        const cam = refPl.current.camera as THREE.PerspectiveCamera

        const _euler_c = new THREE.Euler(0, 0, 0)
        _euler_c.setFromQuaternion(cam.quaternion);
        _euler_c.y = THREE.MathUtils.lerp(_euler_c.y, 0, _alpha_c)
        _euler_c.x = THREE.MathUtils.lerp(_euler_c.x, 0, _alpha_c)
        _euler_c.z = THREE.MathUtils.lerp(_euler_c.z, 0, _alpha_c)
        cam.zoom = THREE.MathUtils.lerp(cam.zoom, config.ZOOM.DEFAULT, _alpha_c)

        cam.quaternion.setFromEuler(_euler_c);
        cam.updateProjectionMatrix()
      }

      return
    }

    /*
     * camera autopilot rotation
     */
    const dlim = 20000
    const now = Date.now()

    if (refCtlsMode.current >= 0 && (now - refLastOperated.current > dlim) && refAutoPilot.current == false) {
      console.log('start autopilot')
      refAutoPilot.current = true
      onAutoPilot(refAutoPilot.current)

      if (ref.current && refCtlsMode.current < 1) {
        // - NOTE: for OrbitControls only
        ref.current.autoRotateSpeed = -0.33
        ref.current.autoRotate = true
        ref.current.update()
      }
    }

    if (refPl.current && refCtlsMode.current < 1) {
      const cam = refPl.current.camera as THREE.PerspectiveCamera

      if (refAutoPilot.current) {
        // - NOTE: for PointerLocControls only
        const _euler = new THREE.Euler(0, 0, 0, "YXZ")
        // const _PI_2 = Math.PI / 2;
        _euler.setFromQuaternion(cam.quaternion);
        _euler.y += 0.00075
        // _euler.x += 0.01
        // _euler.x = Math.max(_PI_2 - refPl.current.maxPolarAngle, Math.min(_PI_2 - refPl.current.minPolarAngle, _euler.x));
        cam.quaternion.setFromEuler(_euler);
      }

      cam.fov = refCameraFov.current
      cam.updateProjectionMatrix()
    }
  })

  return (
    <>
      {(isTouchDevice && controlsMode < 2) && (
        <OrbitControls
          ref={ref}
          autoRotateSpeed={0.2}
          enableDamping={true}
          dampingFactor={0.14}
          rotateSpeed={-0.6}
          enableZoom={false}
          enablePan={false}
          // enabled={controlsEnabled}
        />
      )}
      {(!isTouchDevice && controlsMode < 2) && (
        <PointerLockControls
          ref={refPl}
          enabled={controlsEnabled}
          onLock={() => {}}
        />
      )}
    </>
  )
})
